import { zodTransformFirestoreTimestampToDate } from "../../utils/zodUtils.mjs";
import { zodFlavorId } from "../ids.mjs";
import { defineFirestoreCollection } from "../firebaseCollectionSchema.mjs";
import { executionTraceStampSchema } from "../executionTrace.mjs";
import { bridgeServiceEligibilityStatusPollOutcomeSchema } from "../vendors/bridge/consultSyncTargets.mjs";
import { bridgePatientResponsibilityBreakdownSchema } from "../vendors/bridge/patientResponsibility.mjs";
import { bridgeServiceEligibilityStatusSchema } from "../vendors/bridge/serviceEligibility.mjs";
import { bridgeServiceStatusSchema } from "../vendors/bridge/service.mjs";
import { z as z$1 } from "zod";

// src/models/projections/bridgeCoverageProjection.ts
const firestoreDate = z$1.any().transform(zodTransformFirestoreTimestampToDate);
/** Fixed doc id under `consultations/{consultationId}/projections/bridgeCoverage`. */
const BRIDGE_COVERAGE_PROJECTION_KEY = "bridgeCoverage";
const bridgeServiceEnsureOutcomeSchema = z$1.enum([
	"ok",
	"failed_retriable",
	"failed_permanent"
]);
/**
* Lean FE mirror for insured Bridge coverage at reserve/checkout.
* Path: `consultations/{consultationId}/projections/bridgeCoverage`.
*/
const bridgeCoverageProjectionSchema = z$1.object({
	id: zodFlavorId(),
	status: bridgeServiceEligibilityStatusSchema.optional(),
	patientResponsibility: z$1.number().int().nonnegative().optional(),
	patientResponsibilityBreakdown: bridgePatientResponsibilityBreakdownSchema.optional(),
	finalPatientResponsibility: z$1.number().int().nonnegative().optional(),
	finalPatientResponsibilityBreakdown: bridgePatientResponsibilityBreakdownSchema.optional(),
	finalPaidCents: z$1.number().int().nonnegative().optional(),
	/**
	* Customer-safe settlement remaining (Stripe delta): max(0, Bridge outstanding −
	* registered patient payments). Matches settlement checkout charge amount.
	*/
	settlementRemainingCents: z$1.number().int().nonnegative().optional(),
	reservedSlotKey: z$1.string().optional(),
	seSyncId: zodFlavorId().optional(),
	pollOutcome: bridgeServiceEligibilityStatusPollOutcomeSchema.optional(),
	/** Observed Bridge Service status (L4). */
	serviceStatus: bridgeServiceStatusSchema.optional(),
	/** Bridge Service vendorNativeId when created. */
	serviceNativeId: z$1.string().optional(),
	/** Outcome of Bridge Service ensure binding convergence. */
	serviceEnsureOutcome: bridgeServiceEnsureOutcomeSchema.optional(),
	serviceErrorMessage: z$1.string().optional(),
	/** True when projection reflects current control-plane binding + observed SE. */
	inSync: z$1.boolean().default(false),
	/**
	* Assigned consultant's Bridge provider id ∈ Hard SE `snapshot.providers[]`.
	* Fail-closed default: missing Bridge id, empty/unparseable providers, or miss → false.
	*/
	assignedProviderEligible: z$1.boolean().default(false),
	createdAt: firestoreDate,
	updatedAt: firestoreDate,
	executionTrace: executionTraceStampSchema.optional()
});
const ConsultationProjections = defineFirestoreCollection("projections", bridgeCoverageProjectionSchema, void 0, { executionTrace: true });
/** Pub/Sub ordering key for bridgeCoverage projection resolve. */
function buildBridgeCoverageProjectionOrderingKey(consultationId) {
	return `consultations/${consultationId}/projections/${BRIDGE_COVERAGE_PROJECTION_KEY}`;
}

export { BRIDGE_COVERAGE_PROJECTION_KEY, ConsultationProjections, bridgeCoverageProjectionSchema, buildBridgeCoverageProjectionOrderingKey };
//# sourceMappingURL=bridgeCoverageProjection.mjs.map